import axios from "axios";
import { FAILURE_MESSAGE, SUCCESS_MESSAGE, FILE_RULES } from "@/constants/formOptions";
import { flattenTalentPayloadForMultipart } from "@/lib/submissionPayloads";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

/**
 * Generate a new idempotency key. Callers create one per form-open and reuse it
 * across retries; on a successful submit, they rotate to a fresh key so the
 * next submission is treated as new.
 */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers (RFC 4122 v4-ish)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function idemHeaders(key) {
  return key ? { "Idempotency-Key": key } : {};
}

/**
 * Handle a submission response. Backend returns:
 *   { id, ui_state: "success" | "partial" | "error", message, ... }
 * Convert into a normalized object with `variant` + `message` for the UI.
 */
function normalizeSubmissionResponse(data) {
  const ui = data?.ui_state || "success";
  return {
    ok: ui !== "error",
    variant: ui,
    message:
      data?.message ||
      (ui === "success" || ui === "partial" ? SUCCESS_MESSAGE : FAILURE_MESSAGE),
    id: data?.id || "",
    submission_ref: data?.submission_ref || "",
    raw: data,
  };
}

export const TIMEOUT_OR_UNAVAILABLE_MESSAGE =
  "The server took too long to respond. Your submission may still have been received. Please wait a moment before trying again, or contact SoKno Productions.";

export const NETWORK_FAILURE_MESSAGE =
  "We could not reach the server. Check your connection and try again.";

export function extractErrorMessage(err) {
  const status = err?.response?.status;
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return TIMEOUT_OR_UNAVAILABLE_MESSAGE;
  }
  if (status === 502 || status === 503 || status === 504) {
    return TIMEOUT_OR_UNAVAILABLE_MESSAGE;
  }
  if (!err?.response && /network error/i.test(err?.message || "")) {
    return NETWORK_FAILURE_MESSAGE;
  }
  if (status === 429) {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    return "Too many requests. Please wait a moment and try again.";
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first?.msg) return first.msg;
    if (typeof first === "string") return first;
  }
  return FAILURE_MESSAGE;
}

/** Wake a cold Render instance while the visitor is filling the form. */
export function warmupApi() {
  if (typeof window === "undefined") return Promise.resolve();
  return client.get("/health/live", { timeout: 15000 }).catch(() => null);
}

export const submitGeneralContact = (payload, { idempotencyKey } = {}) =>
  client
    .post("/submissions/contact", payload, { headers: idemHeaders(idempotencyKey) })
    .then((r) => normalizeSubmissionResponse(r.data))
    .catch((err) => {
      throw new Error(extractErrorMessage(err));
    });

export const submitProjectInquiry = (payload, { idempotencyKey } = {}) =>
  client
    .post("/submissions/project", payload, { headers: idemHeaders(idempotencyKey) })
    .then((r) => normalizeSubmissionResponse(r.data))
    .catch((err) => {
      throw new Error(extractErrorMessage(err));
    });

export const submitPartner = (payload, { idempotencyKey } = {}) =>
  client
    .post("/submissions/partner", payload, { headers: idemHeaders(idempotencyKey) })
    .then((r) => normalizeSubmissionResponse(r.data))
    .catch((err) => {
      throw new Error(extractErrorMessage(err));
    });

export const submitCrew = (payload, files, { idempotencyKey } = {}) => {
  const resume = files?.resume_file;
  const headshot = files?.headshot_file;
  if (!resume && !headshot) {
    return client
      .post("/submissions/crew", payload, { headers: idemHeaders(idempotencyKey) })
      .then((r) => normalizeSubmissionResponse(r.data))
      .catch((err) => {
        throw new Error(extractErrorMessage(err));
      });
  }

  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      fd.append(k, JSON.stringify(v));
    } else if (typeof v === "boolean") {
      fd.append(k, String(v));
    } else {
      fd.append(k, v);
    }
  });
  if (resume) fd.append("resume_file", resume);
  if (headshot) fd.append("headshot_file", headshot);

  return client
    .post("/submissions/crew-with-files", fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...idemHeaders(idempotencyKey),
      },
      timeout: 120000,
    })
    .then((r) => normalizeSubmissionResponse(r.data))
    .catch((err) => {
      throw new Error(extractErrorMessage(err));
    });
};

/**
 * Casting submissions come in two flavors:
 *  - JSON only (no files): POST /api/submissions/casting
 *  - Multipart with files: POST /api/submissions/casting-with-files
 * `payload` is the JSON shape; `files` is { headshot_file, resume_file, additional_photo_files }.
 */
export const submitCasting = (payload, files, { idempotencyKey } = {}) => {
  const headshot = files?.headshot_file;
  const resume = files?.resume_file;
  const additionalPhotos = files?.additional_photo_files || [];
  if (!headshot && !resume && !additionalPhotos.length) {
    return client
      .post("/submissions/casting", payload, { headers: idemHeaders(idempotencyKey) })
      .then((r) => normalizeSubmissionResponse(r.data))
      .catch((err) => {
        throw new Error(extractErrorMessage(err));
      });
  }

  const flat = flattenTalentPayloadForMultipart(payload);
  const fd = new FormData();
  Object.entries(flat).forEach(([k, v]) => {
    fd.append(k, v);
  });
  if (headshot) fd.append("headshot_file", headshot);
  if (resume) fd.append("resume_file", resume);
  (additionalPhotos || []).forEach((file) => {
    if (file) fd.append("additional_photo_files", file);
  });

  return client
    .post("/submissions/casting-with-files", fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...idemHeaders(idempotencyKey),
      },
      timeout: 120000,
    })
    .then((r) => normalizeSubmissionResponse(r.data))
    .catch((err) => {
      throw new Error(extractErrorMessage(err));
    });
};

export const healthCheck = () => client.get("/health").then((r) => r.data);

/**
 * Public-facing studio emails for mailto links only.
 * Notification routing uses server env (RESEND_NOTIFICATION_EMAIL) — never hard-code
 * the notification recipient into form submit handlers.
 */
export const SOKNO_EMAILS = {
  general: "info@soknoproductions.com",
  casting: "casting@soknoproductions.com",
  projects: "projects@soknoproductions.com",
  partners: "partners@soknoproductions.com",
};

/**
 * Client-side upload guardrails, matched to backend `ALLOWED_UPLOAD_MIME` / `MAX_UPLOAD_BYTES`.
 */
export const UPLOAD_LIMITS = {
  MAX_BYTES: FILE_RULES.MAX_BYTES,
  ALLOWED_MIME: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
  ACCEPT_ATTR: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
};

export function validateUploadFile(file, kind) {
  if (!file) return null;
  if (!UPLOAD_LIMITS.ALLOWED_MIME.includes((file.type || "").toLowerCase())) {
    return `${kind} must be a PDF, JPG, or PNG file.`;
  }
  if (file.size > UPLOAD_LIMITS.MAX_BYTES) {
    return `${kind} must be 10 MB or smaller.`;
  }
  return null;
}
